// src/app/pages/HomePaciente.tsx
// Versión ajustada a las convenciones del repo (semantic tokens).
// Diferencias vs v1-raw:
//   - hex hardcoded  → tokens Tailwind (bg-background, bg-card, border-border,
//     text-muted-foreground, bg-primary, text-primary-foreground, bg-muted)
//   - bg-[#003B4A]/8 → bg-primary/10 (en Tailwind default no existe /8)
//   - path           → src/app/pages/HomePaciente.tsx (convención del repo)
//   - import Activity sin usar → removido
import {
  ArrowRight,
  CheckCircle2,
  Dumbbell,
  Flame,
  Play,
  PlayCircle,
  Quote,
  Target,
  TrendingUp,
} from "lucide-react"
import { LineChart, Line, ResponsiveContainer } from "recharts"
import { PacienteBottomNav } from "@/app/components/paciente/PacienteBottomNav"
import { PacienteSidebar } from "@/app/components/paciente/PacienteSidebar"
import { RoleToggle } from "@/app/components/RoleToggle"
import { DarkModeToggle } from "@/app/components/DarkModeToggle"
import { isVideoOnlyExercise } from "@/lib/pose/exerciseSchemas"
import type { RomPaciente } from "@/lib/paciente/useEvolucionPaciente"
import logoGonixLight from "@/assets/logo-gonix-light.png"
import logoGonixDark from "@/assets/logo-gonix-dark.png"

// ─── Types ────────────────────────────────────────────────────────────────

export type EstadoEjercicio = "completado" | "actual" | "pendiente"

export interface EjercicioHoy {
  /** ID del ejercicio del catálogo (texto, ej. "rot_externa_banda"). */
  id: string
  /** ID de la fila en plan_ejercicios — necesario para crear la sesión
   *  al click. Distinto del `id` (que es del catálogo de ejercicios). */
  planEjercicioId: string
  nombre: string
  reps: number
  series: number
  estado: EstadoEjercicio
}

export interface HomePacienteProps {
  paciente: { nombre: string; avatar?: string }
  rutinaHoy: { ejercicios: EjercicioHoy[] }
  progresoSemana: { completados: number; rachaDias: number }
  /** Sección desktop "Tu progreso". Sin esto no se renderiza. */
  progreso?: {
    adherenciaPct: number | null
    rachaDias: number
    rom: RomPaciente | null
  }
  mensajeKine?: { texto: string; fecha: string }
  onEmpezarEjercicio: (id: string) => void
  onAbrirMensajes?: () => void
  /** Iniciales del kinesiólogo para el avatar (default "DM") */
  kineIniciales?: string
  /** Nombre del kinesiólogo (default "Dr. Martínez") */
  kineNombre?: string
  /** DEV-only: skippea el ejercicio actual creando una sesión completada
   *  stub. Cuando no se provee (producción / build), el botón no se
   *  renderiza. El wrapper inyecta la versión real con DEV guard. */
  onSkipEjercicio?: (planEjercicioId: string, ejercicioId: string) => void
  /** Loading del skip — desactiva botón mientras está en vuelo. */
  skipLoading?: boolean
}

// ─── Main component ──────────────────────────────────────────────────────

export function HomePaciente({
  paciente,
  rutinaHoy,
  progresoSemana,
  progreso,
  mensajeKine,
  onEmpezarEjercicio,
  onAbrirMensajes,
  kineIniciales = "DM",
  kineNombre = "Dr. Martínez",
  onSkipEjercicio,
  skipLoading = false,
}: HomePacienteProps) {
  const ejercicios = rutinaHoy.ejercicios
  const completados = ejercicios.filter((e) => e.estado === "completado").length
  const total = ejercicios.length
  const pct = Math.round((completados / Math.max(1, total)) * 100)
  const haComenzado = completados > 0
  const actual =
    ejercicios.find((e) => e.estado === "actual") ||
    ejercicios.find((e) => e.estado === "pendiente")
  const duracionEstimadaMin = total * 5
  const primerNombre = paciente.nombre.split(" ")[0]
  const todoHecho = total > 0 && completados === total
  const romSub = progreso?.rom
    ? `${progreso.rom.ejercicioNombre} · ${progreso.rom.semanas.length} sem`
    : "sin mediciones todavía"

  const hoy = new Date().toLocaleDateString("es-AR", {
    weekday: "long",
    day: "numeric",
    month: "short",
  })

  return (
    // Outer: flex-col global. En desktop, el header desktop full-width
    // vive al top (arriba del sidebar), y debajo va el flex row
    // sidebar + main — patrón alineado con el shell del kine
    // (KineHeader full-width + KineSidebar debajo).
    // Mobile mantiene la estructura pre-B4c: columna 480 centered con
    // header inline (fondo degradado) + main + bottom nav.
    <div className="min-h-screen w-full flex flex-col">
      {/* Header DESKTOP — full-width top-bar, aparece por encima del
          sidebar. Idéntico patrón al KineHeader (bg-card + border-b). */}
      <header className="hidden lg:flex items-center justify-between px-8 py-3 border-b border-border bg-card">
        <HeaderInner
          hoy={hoy}
          primerNombre={primerNombre}
          avatar={paciente.avatar}
        />
      </header>

      {/* Body row. Mobile: `justify-center` para centrar la columna 480
          (idéntico al pre-B4c). Desktop: `justify-start` para que sidebar
          + main llenen el ancho. */}
      <div className="flex-1 flex justify-center lg:justify-start">
        <PacienteSidebar />

        {/* Contenedor de contenido paciente. Mobile mantiene el column
            480px centered (con pb-24 para el bottom nav fixed). Desktop:
            se estira ocupando el resto del ancho después del sidebar. */}
        <div className="w-full max-w-[480px] lg:max-w-none flex flex-col bg-background pb-24 lg:pb-0 relative lg:flex-1 lg:min-w-0">
          {/* Fondo degradado del header — solo mobile. En desktop el
              header ya está fuera de este container. */}
          <div className="absolute top-0 left-0 right-0 h-[180px] bg-gradient-to-b from-primary/10 to-transparent pointer-events-none lg:hidden" />

          {/* Header MOBILE — inline dentro del column 480 con el fondo
              degradado por debajo (idéntico al pre-B4c). En desktop está
              oculto porque el header desktop vive al top del outer. */}
          <header className="lg:hidden relative px-5 pt-6 pb-2 flex items-center justify-between">
            <HeaderInner
              hoy={hoy}
              primerNombre={primerNombre}
              avatar={paciente.avatar}
            />
          </header>

          {/* Main content. Mobile: flex column apilando las 4 secciones
            en orden DOM (hero, ejercicios, semana, mensaje).
            Desktop lg:: grid 4 cols alineado con las 4 cards de "Tu
            progreso" de abajo, reordenado por filas con lg:order-*:
            fila 1 = hero (span 2) + tu semana (span 2), fila 2 =
            ejercicios (span 4, grid 2 cols), fila 3 = mensaje kine. */}
        <main className="relative px-5 pt-4 flex flex-col gap-4 lg:px-8 lg:pt-6 lg:pb-6 lg:grid lg:grid-cols-4 lg:gap-4 lg:items-start">
          {/* Hero card. Desktop: compacta (botón a la derecha del título)
              y self-stretch para que su bottom empareje con las cajas de
              Tu semana. */}
          <section className="rounded-3xl bg-primary text-primary-foreground p-6 shadow-sm relative overflow-hidden lg:col-span-2 lg:order-1 lg:self-stretch">
            <div className="absolute -top-8 -right-8 h-36 w-36 rounded-full bg-white/5" />
            <div className="absolute -bottom-12 -left-4 h-32 w-32 rounded-full bg-white/5" />

            <div className="relative lg:h-full lg:grid lg:grid-cols-[1fr_auto] lg:gap-x-6 lg:content-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-primary-foreground/60">
                  {todoHecho ? "¡Día completado!" : "Hoy toca"}
                </p>
                <h2 className="text-3xl font-bold mt-1 leading-tight">
                  {todoHecho
                    ? "Terminaste tu sesión 🎉"
                    : `${total} ejercicio${total === 1 ? "" : "s"}`}
                </h2>
                {!todoHecho && (
                  <p className="text-sm text-primary-foreground/70 mt-1">
                    ≈ {duracionEstimadaMin} minutos
                  </p>
                )}
              </div>

              {!todoHecho && (
                <div className="mt-5 lg:mt-4 lg:col-span-2">
                  <div className="flex justify-between items-baseline mb-2">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-primary-foreground/60">
                      Progreso de hoy
                    </span>
                    <span className="text-sm font-bold tabular-nums">
                      {completados}/{total}
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-white/15 overflow-hidden">
                    <div
                      className="h-full bg-primary-foreground rounded-full transition-all duration-700"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              )}

              {!todoHecho && actual && (
                <button
                  onClick={() => onEmpezarEjercicio(actual.id)}
                  className="mt-5 w-full h-14 rounded-full bg-white text-primary font-bold text-[15px] flex items-center justify-center gap-2 shadow-sm hover:bg-white/95 transition-colors lg:mt-0 lg:w-auto lg:h-12 lg:px-8 lg:row-start-1 lg:col-start-2 lg:self-center"
                >
                  <Play className="h-4 w-4 fill-current" />
                  {haComenzado ? "Continuar" : "Empezar"}
                </button>
              )}
              {todoHecho && (
                <p className="mt-3 text-sm text-primary-foreground/80 leading-relaxed lg:col-span-2">
                  Bien hecho. Mañana hay sesión nueva — descansá.
                </p>
              )}
            </div>
          </section>

          {/* Ejercicios. Desktop: fila completa, grid 2 cols. */}
          <section className="lg:col-span-4 lg:order-3">
            <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground px-1 mb-3">
              Ejercicios de hoy
            </h3>
            <div className="flex flex-col gap-2.5 lg:grid lg:grid-cols-2 lg:gap-4">
              {ejercicios.map((ej) => (
                <EjercicioCard
                  key={ej.id}
                  ejercicio={ej}
                  onEmpezar={() => onEmpezarEjercicio(ej.id)}
                  onSkip={
                    onSkipEjercicio
                      ? () => onSkipEjercicio(ej.planEjercicioId, ej.id)
                      : undefined
                  }
                  skipLoading={skipLoading}
                />
              ))}
            </div>
          </section>

          {/* Progreso semana. Desktop: fila 1, al lado del hero. */}
          <section className="lg:col-span-2 lg:order-2">
            <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground px-1 mb-3">
              Tu semana
            </h3>
            <div className="grid grid-cols-2 gap-2.5 lg:gap-4">
              <MiniStat
                icon={<CheckCircle2 className="h-5 w-5" strokeWidth={2.2} />}
                iconBg="bg-green-50 text-green-600"
                value={progresoSemana.completados}
                label="ejercicios"
                sub="completados"
              />
              <MiniStat
                icon={<Flame className="h-5 w-5" strokeWidth={2.2} />}
                iconBg="bg-amber-50 text-amber-600"
                value={progresoSemana.rachaDias}
                label={progresoSemana.rachaDias === 1 ? "día" : "días"}
                sub="de racha"
              />
            </div>
          </section>

          {/* Mensaje kine. Desktop: fila propia debajo de los ejercicios. */}
          {mensajeKine && (
            <section className="lg:col-span-4 lg:order-4">
              <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground px-1 mb-3">
                De tu kinesiólogo
              </h3>
              <button
                onClick={onAbrirMensajes}
                className="w-full text-left rounded-2xl bg-card border border-border shadow-sm p-4 flex gap-3 hover:border-primary/20 transition-colors"
              >
                <div className="h-10 w-10 rounded-full bg-primary/10 text-primary font-bold text-sm flex items-center justify-center shrink-0">
                  {kineIniciales}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <p className="text-sm font-semibold text-primary">
                      {kineNombre}
                    </p>
                    <p className="text-[11px] text-muted-foreground shrink-0">
                      {mensajeKine.fecha}
                    </p>
                  </div>
                  <div className="relative mt-1">
                    <Quote className="absolute -left-0.5 -top-0.5 h-3 w-3 text-primary/20 fill-current" />
                    <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2 pl-3.5">
                      {mensajeKine.texto}
                    </p>
                  </div>
                </div>
                <ArrowRight
                  className="h-4 w-4 text-muted-foreground shrink-0 mt-3"
                  strokeWidth={2.3}
                />
              </button>
            </section>
          )}
        </main>

        {/* Sección "Tu progreso" — SOLO desktop, full-width debajo del
            main. Datos reales (adherencia RPC + ROM de pose_analysis +
            racha). Mobile no la ve. */}
        {progreso && (
        <section className="hidden lg:block px-8 pb-8">
          <div className="flex items-baseline justify-between mb-3">
            <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              Tu progreso
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <ProgresoStatCard
              icon={<Target className="w-5 h-5 text-primary" strokeWidth={1.75} />}
              iconBg="bg-primary/10"
              value={progreso.adherenciaPct !== null ? `${progreso.adherenciaPct}%` : "—"}
              label="Adherencia"
              sub="desde que empezaste"
              pct={progreso.adherenciaPct ?? undefined}
            />
            <ProgresoStatCard
              icon={<TrendingUp className="w-5 h-5 text-success" strokeWidth={1.75} />}
              iconBg="bg-success/10"
              value={
                progreso.rom
                  ? `${progreso.rom.ganado >= 0 ? "+" : ""}${progreso.rom.ganado}°`
                  : "—"
              }
              label="ROM ganado"
              sub={romSub}
            />
            <ProgresoStatCard
              icon={<Flame className="w-5 h-5 text-amber-600" strokeWidth={1.75} />}
              iconBg="bg-amber-50"
              value={progreso.rachaDias}
              label="Racha activa"
              sub={progreso.rachaDias === 1 ? "día" : "días seguidos"}
            />
            <ProgresoSparklineCard
              data={progreso.rom?.semanas ?? []}
              label="Tendencia ROM"
              sub={romSub}
            />
          </div>
        </section>
        )}
        </div>
      </div>

      {/* Bottom nav — el componente ya lleva lg:hidden internamente.
          Fixed positioning → no importa dónde esté en el DOM, pero
          semánticamente vive al outer (par de header desktop del top). */}
      <PacienteBottomNav />
    </div>
  )
}

// ─── Header inner ───────────────────────────────────────────────────────
// Content del header (logo + fecha/saludo + toggles + avatar) usado por
// las 2 variants del header responsive: el desktop full-width del top y el
// mobile inline del column 480. Extraído para no duplicar el markup.

function HeaderInner({
  hoy,
  primerNombre,
  avatar,
}: {
  hoy: string
  primerNombre: string
  avatar: string | undefined
}) {
  return (
    <>
      <div className="flex items-center gap-3 min-w-0">
        <img
          src={logoGonixLight}
          alt="Gonix"
          className="block dark:hidden h-10 w-auto object-contain shrink-0"
        />
        <img
          src={logoGonixDark}
          alt="Gonix"
          className="hidden dark:block h-10 w-auto object-contain shrink-0"
        />
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            {hoy}
          </p>
          <h1 className="text-xl font-bold text-primary mt-0.5 truncate">
            Hola, {primerNombre} 👋
          </h1>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <RoleToggle />
        <DarkModeToggle />
        <button className="h-10 w-10 rounded-full bg-primary/10 text-primary font-bold text-sm flex items-center justify-center shrink-0 overflow-hidden">
          {avatar ? (
            <img
              src={avatar}
              alt=""
              className="h-full w-full object-cover"
            />
          ) : (
            primerNombre[0]
          )}
        </button>
      </div>
    </>
  )
}

// ─── Ejercicio card ──────────────────────────────────────────────────────

function EjercicioCard({
  ejercicio,
  onEmpezar,
  onSkip,
  skipLoading,
}: {
  ejercicio: EjercicioHoy
  onEmpezar: () => void
  /** DEV-only. Si está, se renderiza el botón "Skip (QA)" en la card actual. */
  onSkip?: () => void
  skipLoading?: boolean
}) {
  const { id, nombre, reps, series, estado } = ejercicio
  // Sprint 3 B4a PR3: para video_only mostramos "Video tutorial" en lugar
  // de "X × Y reps" — defaults 1/1/0 persistidos en DB no aplican
  // conceptualmente al paciente (sigue el video, no cuenta reps).
  const isVideoOnly = isVideoOnlyExercise(id)
  const subtituloEjercicio = isVideoOnly
    ? "Video tutorial"
    : `${series} × ${reps} reps`
  const IconSub = isVideoOnly ? PlayCircle : null

  if (estado === "completado") {
    return (
      <div className="rounded-2xl bg-green-50/80 border border-green-100 p-4 flex items-center gap-3">
        <div className="h-11 w-11 rounded-full bg-green-500 text-white flex items-center justify-center shrink-0">
          <CheckCircle2 className="h-5 w-5" strokeWidth={2.5} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-primary line-through decoration-green-600/30">
            {nombre}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
            {IconSub && <IconSub className="h-3 w-3" strokeWidth={1.75} />}
            {subtituloEjercicio}
          </p>
        </div>
        <span className="text-xs font-semibold text-green-700 shrink-0">
          Listo
        </span>
      </div>
    )
  }

  if (estado === "actual") {
    return (
      <div className="rounded-2xl bg-card border-2 border-primary shadow-[0_4px_20px_rgba(0,59,74,0.08)] p-4">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0">
            <Dumbbell className="h-5 w-5" strokeWidth={2.2} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-primary">{nombre}</p>
            <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
              {IconSub && <IconSub className="h-3 w-3" strokeWidth={1.75} />}
              {subtituloEjercicio}
            </p>
          </div>
          <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider shrink-0">
            <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
            Siguiente
          </span>
          <PlayIconButton nombre={nombre} onClick={onEmpezar} destacado />
        </div>
        {onSkip && (
          <button
            onClick={onSkip}
            disabled={skipLoading}
            className="mt-2 w-full h-9 rounded-full border border-dashed border-muted-foreground/40 text-muted-foreground hover:text-foreground hover:border-muted-foreground font-medium text-xs flex items-center justify-center gap-1.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Crea una sesión completada stub. Solo visible en development."
          >
            {skipLoading ? "Saltando…" : "Saltar (QA)"}
          </button>
        )}
      </div>
    )
  }

  return (
    <div className="rounded-2xl bg-card border border-border shadow-sm p-4 flex items-center gap-3">
      <div className="h-11 w-11 rounded-full bg-muted text-muted-foreground flex items-center justify-center shrink-0">
        <Dumbbell className="h-5 w-5" strokeWidth={2.2} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-primary">{nombre}</p>
        <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
          {IconSub && <IconSub className="h-3 w-3" strokeWidth={1.75} />}
          {subtituloEjercicio}
        </p>
      </div>
      <PlayIconButton nombre={nombre} onClick={onEmpezar} />
    </div>
  )
}

// Botón play circular alineado a la derecha de la card: lanza ESE
// ejercicio. `destacado` = card "Siguiente" (relleno primary).
function PlayIconButton({
  nombre,
  onClick,
  destacado = false,
}: {
  nombre: string
  onClick: () => void
  destacado?: boolean
}) {
  return (
    <button
      onClick={onClick}
      aria-label={`Empezar ${nombre}`}
      title="Empezar"
      className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 transition-colors ${
        destacado
          ? "bg-primary text-primary-foreground hover:bg-primary/90"
          : "bg-primary/10 text-primary hover:bg-primary/20"
      }`}
    >
      <Play className="h-4 w-4 fill-current ml-0.5" />
    </button>
  )
}

// ─── Mini stat ───────────────────────────────────────────────────────────

function MiniStat({
  icon,
  iconBg,
  value,
  label,
  sub,
}: {
  icon: React.ReactNode
  iconBg: string
  value: number
  label: string
  sub: string
}) {
  return (
    <div className="rounded-2xl bg-card border border-border shadow-sm p-4">
      <div
        className={`h-9 w-9 rounded-full ${iconBg} flex items-center justify-center mb-3`}
      >
        {icon}
      </div>
      <p className="text-3xl font-bold text-primary leading-none tabular-nums">
        {value}
      </p>
      <p className="text-xs font-semibold text-primary mt-1.5">{label}</p>
      <p className="text-[11px] text-muted-foreground">{sub}</p>
    </div>
  )
}

// ─── Progreso stat cards (desktop preview section) ───────────────────────
// Placeholder con badge amber "Ejemplo". Cuando lleguen datos reales
// agregados, la card consume el mismo shape pero sin el badge.

function ProgresoStatCard({
  icon,
  iconBg,
  value,
  label,
  sub,
  pct,
}: {
  icon: React.ReactNode
  iconBg: string
  value: string | number
  label: string
  sub: string
  /** Si está, se renderiza una barra horizontal 0..100. Para adherencia. */
  pct?: number
}) {
  return (
    <div className="relative rounded-2xl bg-card border border-border shadow-sm p-4">
      <div className={`h-9 w-9 rounded-full ${iconBg} flex items-center justify-center mb-3`}>
        {icon}
      </div>
      <p className="text-3xl font-bold text-primary leading-none tabular-nums">
        {value}
      </p>
      <p className="text-xs font-semibold text-primary mt-1.5">{label}</p>
      <p className="text-[11px] text-muted-foreground truncate" title={sub}>{sub}</p>
      {pct !== undefined && (
        <div className="mt-3 h-1.5 rounded-full bg-muted overflow-hidden">
          <div
            className="h-full bg-primary rounded-full transition-all duration-500"
            style={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
          />
        </div>
      )}
    </div>
  )
}

function ProgresoSparklineCard({
  data,
  label,
  sub,
}: {
  data: Array<{ week: string; value: number }>
  label: string
  sub: string
}) {
  return (
    <div className="relative rounded-2xl bg-card border border-border shadow-sm p-4">
      <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center mb-3">
        <TrendingUp className="w-5 h-5 text-primary" strokeWidth={1.75} />
      </div>
      <p className="text-xs font-semibold text-primary">{label}</p>
      <p className="text-[11px] text-muted-foreground mb-2 truncate" title={sub}>{sub}</p>
      {/* Recharts requiere altura fija con ResponsiveContainer.
          h-14 = 56px, suficiente para ver la tendencia sin dominar la card. */}
      <div className="h-14">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={data}
            margin={{ top: 4, right: 4, bottom: 4, left: 4 }}
          >
            <Line
              type="monotone"
              dataKey="value"
              stroke="var(--primary)"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

export default HomePaciente
